import { HttpClient } from "@angular/common/http";
import { Injectable, TemplateRef } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MainState } from "@app/store/state";
import { ModeratorDecision } from "@core/interfaces/image.interface";
import { CommonApiService } from "@core/services/api/classic/common/common-api.service";
import { AuthService } from "@core/services/auth.service";
import { LoadingService } from "@core/services/loading.service";
import { UserSubscriptionService } from "@core/services/user-subscription/user-subscription.service";
import { environment } from "@env/environment";
import { selectUser } from "@features/account/store/auth.selectors";
import { ImageEditFieldsBaseService } from "@features/image/services/image-edit-fields-base.service";
import { ImageEditService } from "@features/image/services/image-edit.service";
import { Store } from "@ngrx/store";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, filter, map, take, tap } from "rxjs/operators";

@Injectable({
  providedIn: null
})
export class ImageEditBasicFieldsService extends ImageEditFieldsBaseService {
  collaboratorLabelTemplate: TemplateRef<any>;
  collaboratorOptionTemplate: TemplateRef<any>;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly loadingService: LoadingService,
    public readonly translateService: TranslateService,
    public readonly imageEditService: ImageEditService,
    public readonly commonApiService: CommonApiService,
    public readonly userSubscriptionService: UserSubscriptionService,
    private readonly _http: HttpClient,
    private readonly _authService: AuthService
  ) {
    super(loadingService);
  }

  onFieldsInitialized(): void {}

  getTitleField(): FormlyFieldConfig {
    return {
      key: "title",
      type: "input",
      wrappers: ["default-wrapper"],
      id: "image-title-field",
      props: {
        label: this.translateService.instant("Title"),
        required: true
      }
    };
  }

  // Description field can be one of three types:
  //  - plain textarea (with basic HTML allowed, legacy)
  //  - BBCode (CKEditor)
  //  - ProseMirror (Tiptap)
  getDescriptionPlainField(): FormlyFieldConfig {
    return {
      key: "description",
      type: "textarea",
      wrappers: ["default-wrapper"],
      id: "image-description-field",
      props: {
        label: this.translateService.instant("Description"),
        description: this.translateService.instant("HTML tags are allowed."),
        required: false,
        rows: 10
      }
    };
  }

  getDescriptionBBCodeField(): FormlyFieldConfig {
    return {
      key: "descriptionBbcode",
      type: "ckeditor",
      wrappers: ["default-wrapper"],
      id: "image-description-field",
      props: {
        label: this.translateService.instant("Description"),
        required: false
      }
    };
  }

  getDescriptionProsemirrorField(): FormlyFieldConfig {
    return {
      key: "descriptionProsemirror",
      type: "tiptap",
      wrappers: ["default-wrapper"],
      id: "image-description-field",
      props: {
        label: this.translateService.instant("Description"),
        placeholder: this.translateService.instant(
          "Describe your image, processing details, or any story behind it..."
        ),
        required: false,
        height: "300px",
        onContentChange: ({ json, html, text }) => {
          // Update the description field with HTML for backward compatibility
          this.imageEditService.model.descriptionHtml = html;
        }
      }
    };
  }

  getDescriptionField(): FormlyFieldConfig {
    if (this.imageEditService.model.descriptionProsemirror) {
      return this.getDescriptionProsemirrorField();
    }

    if (this.imageEditService.model.descriptionBbcode) {
      return this.getDescriptionBBCodeField();
    }

    if (this.imageEditService.model.description) {
      return this.getDescriptionPlainField();
    }

    // Default to ProseMirror for new images
    return this.getDescriptionProsemirrorField();
  }

  getPendingCollaboratorsField(): FormlyFieldConfig {
    interface AutocompleteUser {
      id: string;
      username: string;
      realName: string;
      displayName: string;
      avatar: string;
    }

    const ngSelectData = (user: AutocompleteUser): { value: number; label: string; avatar: string } => ({
      value: parseInt(user.id, 10),
      label: user.displayName !== user.username ? `${user.displayName} (${user.username})` : user.username,
      avatar: user.avatar
    });

    const _getField = (): FormlyFieldConfig => {
      return this.imageEditService.fields[0].fieldGroup[0].fieldGroup[2];
    };

    // Check if user is on free tier AND image is not approved AND no existing collaborators
    const shouldDisableField$ = this.userSubscriptionService.isFree$().pipe(
      map(isFree => {
        const isNotApproved =
          !this.imageEditService.model.moderatorDecision ||
          this.imageEditService.model.moderatorDecision !== ModeratorDecision.APPROVED;
        const hasExistingCollaborators =
          this.imageEditService.model.pendingCollaborators &&
          this.imageEditService.model.pendingCollaborators.length > 0;
        return isFree && isNotApproved && !hasExistingCollaborators;
      })
    );

    return {
      key: "pendingCollaborators",
      type: "ng-select",
      wrappers: ["default-wrapper"],
      id: "image-pending-collaborators-field",
      hooks: {
        onInit: (field: FormlyFieldConfig) => {
          const allCollaboratorIds = [
            ...(this.imageEditService.model.collaborators?.map(c => c.id) || []),
            ...(this.imageEditService.model.pendingCollaborators || [])
          ];

          field.formControl.setValue(allCollaboratorIds);

          shouldDisableField$.pipe(take(1)).subscribe(shouldDisable => {
            if (shouldDisable) {
              field.props.disabled = true;
              field.props.description = this.translateService.instant(
                "Free users can only add new collaborators to images that have been approved by moderators."
              );
            }
          });
        }
      },
      props: {
        label: this.translateService.instant("Collaborators"),
        description: this.translateService.instant(
          "If this image was a group effort, please add additional collaborators (other than you) here."
        ),
        multiple: true,
        required: false,
        clearable: true,
        enableFullscreen: false,
        striped: true,
        labelTemplate: this.collaboratorLabelTemplate,
        optionTemplate: this.collaboratorOptionTemplate,
        options: (() => {
          const confirmedCollaborators = this.imageEditService.model.collaborators || [];
          const pendingCollaboratorIds = this.imageEditService.model.pendingCollaborators || [];

          const confirmedCollaboratorIds = confirmedCollaborators.map(c => c.id);
          const uniquePendingIds = pendingCollaboratorIds.filter(id => !confirmedCollaboratorIds.includes(id));

          const confirmedOptions = confirmedCollaborators.map(collaborator =>
            of({
              value: collaborator.id,
              label:
                collaborator.displayName !== collaborator.username
                  ? `${collaborator.displayName} (${collaborator.username})`
                  : collaborator.username,
              avatar: collaborator.avatar
            })
          );

          const pendingOptions = uniquePendingIds.map(pendingCollaboratorId =>
            this.commonApiService.getUserProfileByUserId(pendingCollaboratorId).pipe(
              map(userProfile => ({
                value: userProfile.user,
                label: userProfile.realName
                  ? `${userProfile.realName} (${userProfile.username})`
                  : userProfile.username,
                avatar: userProfile.avatar
              }))
            )
          );

          const allOptions = [...confirmedOptions, ...pendingOptions];
          return allOptions.length > 0 ? forkJoin(allOptions) : of([]);
        })(),
        onSearch: (term: string): Observable<AutocompleteUser[]> => {
          if (term.length < 2) {
            return of([]);
          }

          const field = _getField();
          const token = this._authService.getClassicApiToken();
          const url = `${environment.classicBaseUrl}/autocomplete_usernames/?q=${encodeURIComponent(term)}&token=${token}`;

          return this._http.get<AutocompleteUser[]>(url).pipe(
            tap(users => {
              field.props = {
                ...field.props,
                options: of(users.map(user => ngSelectData(user)))
              };
            }),
            catchError(() => of([]))
          );
        }
      },
      asyncValidators: {
        notIncludeSelf: {
          expression: (control: FormControl) => {
            return this.store$.select(selectUser, this.imageEditService.model.user).pipe(
              filter(user => !!user),
              take(1),
              map(user => !control.value || control.value.indexOf(user.id) === -1)
            );
          },
          message: this.translateService.instant("No need to include yourself as a collaborator.")
        }
      }
    };
  }

  getLinkField(): FormlyFieldConfig {
    return {
      key: "link",
      type: "input",
      wrappers: ["default-wrapper"],
      id: "image-link-field",
      props: {
        label: this.translateService.instant("Link"),
        description: this.translateService.instant(
          "If you're hosting a copy of this image on your website, put the address here."
        ),
        placeholder: this.translateService.instant("e.g.") + " https://www.example.com/my-page.html",
        required: false
      },
      validators: {
        validation: ["url"]
      }
    };
  }

  getLinkToFitsField(): FormlyFieldConfig {
    return {
      key: "linkToFits",
      type: "input",
      wrappers: ["default-wrapper"],
      id: "image-link-to-fits-field",
      props: {
        label: this.translateService.instant("Link to TIFF/FITS"),
        description: this.translateService.instant(
          "If you want to share the TIFF or FITS file of your image, put a link to the file here. " +
            "Unfortunately, AstroBin cannot offer to store these files at the moment, so you will have to " +
            "host them on your personal space."
        ),
        placeholder: this.translateService.instant("e.g.") + " https://www.example.com/my-page.html",
        required: false
      },
      validators: {
        validation: ["url"]
      }
    };
  }
}
